<div class="settings-page">
    <!-- Page Header -->
    <header class="page-header">
        <h1 class="page-title">Organization settings</h1>
        <div class="organization-name" [cmIsLoading]="$isLoading()">
            @if ($organization()?.name) {
                <p class="organization-name-text">{{ $organization()?.name }}</p>
            }
        </div>
    </header>

    <!-- Main Content -->
    <main class="settings-main">
        @if ($isSelfServicePreview()) {
            <div class="preview-notice">
                <i class="fas fa-circle-info preview-notice-icon"></i>
                <span
                    >Staff preview — billing and team members reflect the real {{ $organization()?.name }} organization, not the previewed self-service
                    account.</span
                >
            </div>
        }

        <!-- Billing Section -->
        <section class="settings-section">
            <div class="section-content">
                <div class="field-group">
                    <h2 class="field-group-title">
                        <i class="field-group-icon fas fa-credit-card"></i>
                        Billing and subscription
                    </h2>

                    <!-- Subscription Content -->
                    <div class="field-row subscription-row" [cmIsLoading]="$isLoading() || $checkoutSuccess()">
                        @if (!stripeFullyIntegrated) {
                            <!-- Stripe's subscription webhook isn't registered outside qa/prod, so the
                                 status here wouldn't reflect reality. Hide it entirely to avoid confusion. -->
                            <div class="stripe-notice">
                                <i class="fas fa-triangle-exclamation stripe-notice-icon"></i>
                                <span
                                    >Stripe is not fully integrated in this environment, so subscription and billing state isn't shown here. Test the full
                                    billing flow on QA or production.</span
                                >
                            </div>
                        } @else if ($organization(); as org) {
                            @if ($hasActiveSubscription()) {
                                <!-- Has active subscription: Show current plan info -->
                                <div class="subscription-container">
                                    <!-- Top row: Label + Plan name + Status badge + Manage button -->
                                    <div class="subscription-header">
                                        <div class="subscription-header-left">
                                            <span class="subscription-label">Current subscription:</span>
                                            <span class="subscription-plan-name">{{ $planName() }}</span>
                                            <span class="status-indicator" [class]="getStatusClass(org.stripeSubscriptionStatus)">{{
                                                getStatusDisplay(org.stripeSubscriptionStatus)
                                            }}</span>
                                        </div>
                                        <div class="subscription-header-right">
                                            <cm-button (click)="openStripeBillingPortal()" [disabled]="$isLoadingBillingPortal()" class="action-button">
                                                <ng-container cm-left-icon>
                                                    @if ($isLoadingBillingPortal()) {
                                                        <cm-loading-spinner-icon></cm-loading-spinner-icon>
                                                    } @else {
                                                        <i class="fas fa-external-link-alt"></i>
                                                    }
                                                </ng-container>
                                                <ng-container cm-text>
                                                    @if ($isLoadingBillingPortal()) {
                                                        Opening...
                                                    } @else {
                                                        Manage billing
                                                    }
                                                </ng-container>
                                            </cm-button>
                                        </div>
                                    </div>

                                    <!-- Subscription details below header -->
                                    <div class="subscription-details">
                                        <!-- Trial indicator (shown first) -->
                                        @if ($isOnTrial()) {
                                            <div class="trial-info" [class]="getTrialUrgencyClass(getTrialDaysRemaining($trialEndDate()))">
                                                <i class="trial-icon fas fa-clock"></i>
                                                @if (getTrialDaysRemaining($trialEndDate()); as daysRemaining) {
                                                    <span class="trial-text"
                                                        >{{ daysRemaining }} day{{ daysRemaining > 1 ? "s" : "" }} remaining in free trial</span
                                                    >
                                                } @else {
                                                    <span class="trial-text">Trial ends today</span>
                                                }
                                                @if ($trialEndDate(); as trialEnd) {
                                                    <span class="trial-end-date">(ends {{ formatDate(trialEnd) }})</span>
                                                }
                                            </div>

                                            <!-- End the trial and start paying now, rather than waiting for the trial to elapse. -->
                                            <div class="trial-upgrade">
                                                <p class="trial-upgrade-hint">End your free trial early and start your paid plan now.</p>
                                                <button class="cm-upgrade-button" (click)="upgradeNow()" [disabled]="$isUpgradingTrial()">
                                                    @if ($isUpgradingTrial()) {
                                                        <cm-loading-spinner-icon></cm-loading-spinner-icon>
                                                        Starting your plan...
                                                    } @else {
                                                        <i class="fas fa-rocket"></i>
                                                        Upgrade now
                                                    }
                                                </button>
                                            </div>
                                        }

                                        @if (org.stripeCurrentPeriodEnd && !$isOnTrial()) {
                                            <div class="billing-period">
                                                @if (org.stripeCancelAtPeriodEnd) {
                                                    <span class="period-text period-text-warning"
                                                        >Subscription ends: {{ formatDate(org.stripeCurrentPeriodEnd) }}</span
                                                    >
                                                } @else {
                                                    <span class="period-text">Next billing: {{ formatDate(org.stripeCurrentPeriodEnd) }}</span>
                                                }
                                            </div>
                                        }
                                        @if ($hasPaymentProblem()) {
                                            <!-- past_due/unpaid: the "Manage billing" button above opens the portal to fix payment. -->
                                            <div class="cancellation-notice">
                                                <i class="cancellation-notice-icon fas fa-exclamation-triangle"></i>
                                                <span class="cancellation-notice-text"
                                                    >Your last payment failed. Update your payment method to keep your paid plan.</span
                                                >
                                            </div>
                                        }
                                        @if (org.stripeCancelAtPeriodEnd) {
                                            <div class="cancellation-notice">
                                                <i class="cancellation-notice-icon fas fa-exclamation-triangle"></i>
                                                <span class="cancellation-notice-text">Your subscription will not renew automatically.</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            } @else {
                                <!-- No live Stripe subscription. This covers a card-less free trial (trialEndsAt set,
                                     no subscription yet) and the Free plan (never subscribed, trial over, or a
                                     canceled or expired subscription). A trial is presented as the current (trial)
                                     subscription with an immediate paid upgrade; the plan cards below let the user
                                     pick the paid plan they upgrade to. -->
                                <div class="no-subscription-container">
                                    @if ($isOnTrial()) {
                                        <!-- Trial framed as the current subscription -->
                                        <div class="subscription-header">
                                            <div class="subscription-header-left">
                                                <span class="subscription-label">Current subscription:</span>
                                                <span class="subscription-plan-name">Free trial</span>
                                                <span class="status-indicator status-active">Trial</span>
                                            </div>
                                        </div>
                                    } @else if ($isFreePlan()) {
                                        <div class="subscription-header">
                                            <div class="subscription-header-left">
                                                <span class="subscription-label">Current plan:</span>
                                                <span class="subscription-plan-name">Free</span>
                                            </div>
                                        </div>
                                    }

                                    <!-- Free trial indicator (only show if on trial) -->
                                    @if (org.trialEndsAt) {
                                        @if (getTrialDaysRemaining(org.trialEndsAt); as daysRemaining) {
                                            <div class="trial-info" [class]="getTrialUrgencyClass(daysRemaining)">
                                                <i class="trial-icon fas fa-clock"></i>
                                                <span class="trial-text"
                                                    >{{ daysRemaining }} day{{ daysRemaining > 1 ? "s" : "" }} remaining in free trial</span
                                                >
                                                <span class="trial-end-date">(ends {{ formatDate(org.trialEndsAt) }})</span>
                                            </div>
                                        } @else {
                                            <div class="trial-info">
                                                <i class="trial-icon fas fa-circle-info"></i>
                                                <span class="trial-text">Your free trial has ended</span>
                                                <span class="trial-end-date">(ended {{ formatDate(org.trialEndsAt) }})</span>
                                            </div>
                                        }
                                    }

                                    <!-- Plan Selection -->
                                    @if ($subscriptionPlans().length > 0) {
                                        <div class="plan-selection-container">
                                            <label class="field-label">
                                                @if ($isOnTrial()) {
                                                    Switch to a paid plan
                                                } @else {
                                                    Upgrade to a paid plan
                                                }
                                            </label>

                                            <!-- Billing Interval Toggle -->
                                            <div class="cm-billing-interval">
                                                <span
                                                    class="cm-interval-label"
                                                    [class.cm-active]="$billingInterval() === 'monthly'"
                                                    (click)="$billingInterval.set('monthly')"
                                                >
                                                    Monthly
                                                </span>
                                                <div
                                                    class="cm-interval-toggle"
                                                    [class.cm-active]="$billingInterval() === 'yearly'"
                                                    (click)="$billingInterval.set($billingInterval() === 'monthly' ? 'yearly' : 'monthly')"
                                                >
                                                    <div class="cm-interval-toggle-slider" [class.cm-active]="$billingInterval() === 'yearly'"></div>
                                                </div>
                                                <span
                                                    class="cm-interval-label"
                                                    [class.cm-active]="$billingInterval() === 'yearly'"
                                                    (click)="$billingInterval.set('yearly')"
                                                >
                                                    Yearly
                                                    @if ($savingsPercentage(); as pct) {
                                                        <span class="cm-save-badge">Save {{ pct }}%</span>
                                                    }
                                                </span>
                                            </div>

                                            <!-- Plan Cards -->
                                            <div class="cm-plan-selection">
                                                @for (plan of $subscriptionPlans(); track plan.productId) {
                                                    <div
                                                        class="cm-plan-card"
                                                        [class.cm-selected]="$selectedPlan()?.productId === plan.productId"
                                                        (click)="$selectedPlan.set(plan)"
                                                    >
                                                        <div class="cm-plan-header">
                                                            <h4 class="cm-plan-name">{{ plan.productName }}</h4>
                                                            <div class="cm-plan-price">
                                                                @if ($billingInterval() === "monthly") {
                                                                    <span class="cm-price-amount"
                                                                        >{{ getCurrencySymbol(plan.monthlyPrice.currency)
                                                                        }}{{ (plan.monthlyPrice.unitAmount || 0) / 100 }}</span
                                                                    >
                                                                    <span class="cm-price-period">/month</span>
                                                                } @else {
                                                                    <span class="cm-price-amount"
                                                                        >{{ getCurrencySymbol(plan.yearlyPrice.currency)
                                                                        }}{{ (plan.yearlyPrice.unitAmount || 0) / 100 }}</span
                                                                    >
                                                                    <span class="cm-price-period">/year</span>
                                                                }
                                                            </div>
                                                        </div>

                                                        @if (plan.productDescription) {
                                                            <p class="cm-plan-description">{{ plan.productDescription }}</p>
                                                        }

                                                        @if (plan.features && plan.features.length > 0) {
                                                            <div class="cm-plan-features">
                                                                @for (feature of plan.features; track $index) {
                                                                    <div class="cm-plan-feature">
                                                                        <i class="far fa-check"></i>
                                                                        <span>{{ feature }}</span>
                                                                    </div>
                                                                }
                                                            </div>
                                                        }
                                                    </div>
                                                }

                                                <!-- Enterprise Plan -->
                                                <div class="cm-plan-card cm-enterprise">
                                                    <div class="cm-plan-header">
                                                        <h4 class="cm-plan-name">Enterprise</h4>
                                                    </div>

                                                    <p class="cm-plan-description">
                                                        The ultimate version of the platform featuring advanced automation, high-performance tools and a lot
                                                        more features. This plan is required for all organizations with annual revenue exceeding $2M
                                                    </p>

                                                    <div class="cm-plan-features">
                                                        <div class="cm-plan-feature">
                                                            <i class="far fa-check"></i>
                                                            <span>Custom integrations</span>
                                                        </div>
                                                        <div class="cm-plan-feature">
                                                            <i class="far fa-check"></i>
                                                            <span>SLA & priority support</span>
                                                        </div>
                                                    </div>

                                                    <a href="https://colormass.com/contact" target="_blank" rel="noopener noreferrer" class="cm-contact-button">
                                                        <i class="far fa-envelope"></i>
                                                        Contact Us
                                                    </a>
                                                </div>
                                            </div>

                                            <!-- Upgrade Button -->
                                            <div class="subscribe-button-container">
                                                @if ($isOnTrial()) {
                                                    <p class="trial-upgrade-hint">End your free trial early and start your paid plan now.</p>
                                                }
                                                <button
                                                    class="cm-upgrade-button"
                                                    (click)="redirectToStripeCheckout()"
                                                    [disabled]="$isLoadingCheckout() || !$selectedPlan()"
                                                >
                                                    @if ($isLoadingCheckout()) {
                                                        <cm-loading-spinner-icon></cm-loading-spinner-icon>
                                                        Processing...
                                                    } @else {
                                                        <i class="fas fa-rocket"></i>
                                                        @if ($isOnTrial()) {
                                                            Upgrade to paid plan
                                                        } @else {
                                                            Upgrade your plan
                                                        }
                                                    }
                                                </button>
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                        }
                    </div>
                </div>
            </div>
        </section>

        <!-- Team Members Section -->
        <section class="settings-section">
            <div class="section-content">
                <div class="field-group">
                    <h2 class="field-group-title">
                        <i class="field-group-icon fas fa-users"></i>
                        Team Members
                    </h2>

                    <!-- Member count and add button -->
                    <div class="field-row" [cmIsLoading]="$isLoadingMembers()">
                        <div class="field-info">
                            <label class="field-label">Organization members</label>
                            <p class="field-description">
                                @if ($organization(); as org) {
                                    @if (org.membersCount) {
                                        {{ org.membersCount }} member{{ org.membersCount > 1 ? "s" : "" }} in this organization
                                    } @else {
                                        No members found
                                    }
                                }
                            </p>
                        </div>
                        <div class="field-actions">
                            <cm-button (click)="openAddUserDialog()" [disabled]="$isLoadingMembers() || !$canAddUser()" class="action-button">
                                <ng-container cm-left-icon>
                                    @if ($isLoadingMembers()) {
                                        <cm-loading-spinner-icon></cm-loading-spinner-icon>
                                    } @else {
                                        <i class="fas fa-user-plus"></i>
                                    }
                                </ng-container>
                                <ng-container cm-text> Add User </ng-container>
                            </cm-button>
                        </div>
                    </div>

                    <!-- Members list -->
                    <div class="field-row" [cmIsLoading]="$isLoadingMembers()">
                        @if ($organization()?.members?.length) {
                            <div class="members-list">
                                @for (member of $organization()?.members; track member.id) {
                                    <div class="member-item">
                                        <div class="member-info">
                                            <div class="member-name">
                                                {{ member.user.firstName }} {{ member.user.lastName }}
                                                @if (isCurrentUser(member.user.id)) {
                                                    <span class="current-user-indicator"> (you)</span>
                                                }
                                            </div>
                                            <div class="member-email">{{ member.user.email }}</div>
                                        </div>
                                        <div class="member-role">
                                            <span class="role-badge" [class]="'role-' + member.role.toLowerCase()">
                                                {{ member.role }}
                                            </span>
                                        </div>
                                    </div>
                                }
                            </div>
                        } @else if (!$isLoadingMembers()) {
                            <div class="field-info">
                                <p class="field-description">No members to display.</p>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </section>
    </main>
</div>
